/**
 * MONSTER TOWN — ROOM SHELL. OWNED BY THE INTERIORS CREW.
 *
 * The dollhouse. Floor, skirting, four walls with doorways cut into them, and
 * the CUT-AWAY rule that makes a fixed isometric interior readable: the two
 * walls nearest the camera are hidden, and they change as the kid turns the
 * world in 90-degree snaps. Without this you are looking at the back of a box.
 *
 *   rot 0  camera sits east + south  -> hide E and S
 *   rot 1  camera sits east + north  -> hide E and N
 *   rot 2  camera sits west + north  -> hide W and N
 *   rot 3  camera sits west + south  -> hide W and S
 *
 * (Derived from `IsoCamera`: position = target + (sin yaw, .., cos yaw) * dist,
 *  base yaw 45 degrees, +90 per rot.)
 *
 * The way out is NOT drawn here. Doorway mats are real placeables (see the
 * `exitMat` def in `src/content/furniture.ts`, kept in place by
 * `ensureExitMats` in `house.ts`) so they can be tapped, so they survive the
 * cut-away, and so a door frame can hide with its wall instead of standing in
 * the middle of the floor.
 */

import * as THREE from 'three'
import type { Rot } from '../core/grid'
import { hex, mix } from '../core/palette'
import type { SceneNode } from '../core/scene'
import { boxOn, group, planeGeo, toon } from '../render/kit'
import type { RoomDef, Side } from './rooms'
import { floorStyle, surfaceMaterial, surfaceRepeat, wallStyle } from './style'

const WALL_H = 2.2
const WALL_T = 0.22
const DOOR_W = 1.15
const DOOR_H = 1.7
/** How far the base slab overhangs the walls. Gives the dollhouse thickness. */
const PLINTH = 0.3
/**
 * CLEARANCE BETWEEN THE GRID EDGE AND THE WALL FACE.
 *
 * Walls used to sit exactly on the grid boundary, so a 0.9-wide piece of
 * furniture in an edge cell left a 0.05 gap — at an isometric angle that is
 * indistinguishable from the piece being buried in the plaster, and it was
 * called out as "the kitchen counter clips into the left wall corner". Pushing
 * the wall out a sixth of a tile gives EVERY edge piece breathing room, now and
 * for any furniture added later, without touching a single art builder.
 */
const WALL_GAP = 0.16

/**
 * THE CORNER POST. Half the width of the wall it caps plus a little, standing at
 * the point where two wall LINES cross.
 *
 * Round-2 blocker, fixed here. Wall runs used to be extended past the room by a
 * whole `WALL_GAP + WALL_T` so the four corners would overlap and not open up.
 * That works right up until the cut-away hides the perpendicular wall — and then
 * the extension is a grey stub of geometry sticking out into open air with
 * nothing attached to it ("an orphan grey stub at x~1745,y~790", "a grey post
 * stub sits at each wall end"). Worse, at the two corners where both walls are
 * drawn the overlap of two textured boxes is exactly the "lighter band at the
 * corner" and the "backdrop shows THROUGH the join between the two walls".
 *
 * A post fixes all three at once: runs now stop exactly ON the corner line so
 * they cannot overlap or fall short, and the post covers the joint from every
 * angle. It survives its neighbours being hidden because it is a post — that is
 * what a post looks like. It is hidden only when BOTH of its walls are cut away,
 * which is the near corner that would otherwise stand in front of the room.
 */
const POST = WALL_T + 0.14
/** Finishes FLUSH with the wall top once its cap is on — never proud of it. */
const POST_H = WALL_H - 0.1

/**
 * Walls NEVER cast shadows. The sun sits over one corner of the world, so a
 * wall that casts would throw the whole room into gloom the moment a kid turns
 * the camera — half the rotations would look like a different, darker game.
 * They still RECEIVE, so furniture shadows fall on them properly.
 */
function noCast(o: THREE.Object3D): THREE.Object3D {
  o.traverse((c) => {
    const m = c as THREE.Mesh
    if (m.isMesh === true) m.castShadow = false
  })
  return o
}

/** Which two walls are between the camera and the room, per camera rotation. */
const HIDDEN_BY_ROT: Record<Rot, readonly Side[]> = {
  0: ['E', 'S'],
  1: ['E', 'N'],
  2: ['W', 'N'],
  3: ['W', 'S'],
}

let hintMat: THREE.LineBasicMaterial | null = null
function gridHintMaterial(): THREE.LineBasicMaterial {
  if (hintMat === null) {
    hintMat = new THREE.LineBasicMaterial({
      color: hex('inkSoft'),
      transparent: true,
      opacity: 0.07,
    })
  }
  return hintMat
}

interface Span {
  from: number
  to: number
}

/** Cut the doorway holes out of one wall run and return the solid spans left. */
function spansFor(len: number, gaps: readonly number[]): Span[] {
  const out: Span[] = []
  let cursor = 0
  const sorted = [...gaps].sort((a, b) => a - b)
  for (const g of sorted) {
    const start = g + 0.5 - DOOR_W / 2
    const end = g + 0.5 + DOOR_W / 2
    if (start > cursor) out.push({ from: cursor, to: start })
    cursor = Math.max(cursor, end)
  }
  if (cursor < len) out.push({ from: cursor, to: len })
  return out
}

/**
 * Build one wall run. `side` decides the axis; `originX/originZ` are the grid's
 * world corner. Doorways become gaps with a frame and a lintel over the top.
 */
function buildWall(
  side: Side,
  room: RoomDef,
  ox: number,
  oz: number,
  wStyleIdx: number,
  trim: number,
): THREE.Group {
  const g = group(`wall${side}`)
  const horizontal = side === 'N' || side === 'S'
  const len = horizontal ? room.width : room.depth
  const gaps = room.doors.filter((d) => d.side === side).map((d) => d.at)
  const spans = spansFor(len, gaps)
  /*
   * Runs reach the CORNER LINE and stop — half a wall thickness plus the gap,
   * which is exactly where the perpendicular wall's centre-line runs. Not one
   * millimetre more (that is the stub) and not one less (that is the hole). The
   * corner post covers the joint. See the POST comment at the top of the file.
   */
  const reach = WALL_GAP + WALL_T / 2
  const first = spans[0]
  const last = spans[spans.length - 1]
  if (first !== undefined && first.from === 0) first.from = -reach
  if (last !== undefined && last.to === len) last.to = len + reach

  // Fixed world coordinate of the wall line (the axis it does NOT run along).
  const fixed =
    side === 'N' ? oz - WALL_T / 2 - WALL_GAP
    : side === 'S' ? oz + room.depth + WALL_T / 2 + WALL_GAP
    : side === 'W' ? ox - WALL_T / 2 - WALL_GAP
    : ox + room.width + WALL_T / 2 + WALL_GAP

  const style = wallStyle(wStyleIdx)
  const place = (from: number, to: number, h: number, y: number): void => {
    const mid = (from + to) / 2
    const l = to - from
    if (l <= 0.001) return
    const mesh =
      horizontal ?
        boxOn(l, h, WALL_T, { color: 'wallCream', x: ox + mid, y, z: fixed })
      : boxOn(WALL_T, h, l, { color: 'wallCream', x: fixed, y, z: oz + mid })
    // TEXTURE SCALE FOLLOWS THE PIECE, NOT THE ROOM. One material shared by a
    // 9-unit run and a 2-unit run stretches the same stripe count over both, so
    // the two halves of a doorway wall had different stripe widths and the two
    // walls of a corner disagreed with each other. Sizing per run costs a few
    // cached materials and makes the paint match everywhere.
    mesh.material = surfaceMaterial(style, surfaceRepeat(l), surfaceRepeat(h))
    g.add(mesh)
  }

  for (const s of spans) place(s.from, s.to, WALL_H, 0)
  // Lintel over each doorway.
  for (const gp of gaps) {
    const c = gp + 0.5
    place(c - DOOR_W / 2, c + DOOR_W / 2, WALL_H - DOOR_H, DOOR_H)
  }

  /**
   * BASEBOARD AND CORNICE. A wall painted one flat colour, lit by one even
   * ambient, reads as untextured cardboard whatever pattern is on it — the
   * pattern is a whisper and the silhouette is a rectangle. Two proud bands give
   * the wall a bottom and a top edge that catch the light differently from the
   * face, which is what actually makes a wall look like a wall.
   */
  const band = (from: number, to: number, h: number, y: number, out: number): void => {
    const mid = (from + to) / 2
    const l = to - from
    if (l <= 0.001) return
    const th = WALL_T + out
    const m =
      horizontal ?
        boxOn(l, h, th, { color: trim, x: ox + mid, y, z: fixed })
      : boxOn(th, h, l, { color: trim, x: fixed, y, z: oz + mid })
    m.castShadow = false
    g.add(m)
  }
  // Baseboard per span (a doorway has no skirting across it). Sunk 2cm so its
  // underside is not coplanar with the floor plane it meets.
  for (const s of spans) band(s.from, s.to, 0.21, -0.02, 0.06)
  /*
   * THE CORNICE IS ONE CONTINUOUS RUN, INCLUDING OVER THE DOORWAYS.
   *
   * It used to be drawn per span, so it stopped dead at every doorway and picked
   * up again on the far side. At the top of a wall that reads as a notch, and
   * where two walls met, one had a cap and the other did not — the critic's
   * "the two walls meet with a notch at the apex" and "the top-rail cap changes
   * colour between them". Its top also sat exactly ON the wall top, two coplanar
   * faces fighting for the same depth: "a thin white z-fighting line along the
   * far wall top". It now runs the whole length and finishes 3cm short of the
   * wall top, so there is nothing to notch and nothing to fight.
   */
  const capFrom = first?.from ?? -reach
  const capTo = last?.to ?? len + reach
  band(capFrom, capTo, 0.11, WALL_H - 0.14, 0.05)
  return g
}

/**
 * A soft dark gradient hugging the inside foot of one wall. Contact shading —
 * "interior lighting is flat with no ambient occlusion". Four transparent quads
 * per room, unlit, so they cost nothing and never swing with the camera.
 */
let aoMat: THREE.Material | null = null
function wallAoMaterial(): THREE.Material {
  if (aoMat === null) {
    const S = 128
    const canvas = document.createElement('canvas')
    canvas.width = S
    canvas.height = S
    const c = canvas.getContext('2d')
    if (c === null) throw new Error('interior shell: no 2d context')
    c.clearRect(0, 0, S, S)
    // An INNER SHADOW: four edge gradients composited into one quad, so the
    // whole skirt is a single draw call and no per-side UV orientation can be
    // got wrong. Corners get both gradients, which is exactly where a real room
    // is darkest.
    const band = S * 0.2
    const ramp = (x0: number, y0: number, x1: number, y1: number): CanvasGradient => {
      const g = c.createLinearGradient(x0, y0, x1, y1)
      g.addColorStop(0, 'rgba(43,36,56,0.36)')
      g.addColorStop(0.35, 'rgba(43,36,56,0.16)')
      g.addColorStop(1, 'rgba(43,36,56,0)')
      return g
    }
    c.fillStyle = ramp(0, 0, 0, band)
    c.fillRect(0, 0, S, band)
    c.fillStyle = ramp(0, S, 0, S - band)
    c.fillRect(0, S - band, S, band)
    c.fillStyle = ramp(0, 0, band, 0)
    c.fillRect(0, 0, band, S)
    c.fillStyle = ramp(S, 0, S - band, 0)
    c.fillRect(S - band, 0, band, S)
    const tex = new THREE.CanvasTexture(canvas)
    tex.colorSpace = THREE.SRGBColorSpace
    tex.wrapS = THREE.ClampToEdgeWrapping
    tex.wrapT = THREE.ClampToEdgeWrapping
    aoMat = new THREE.MeshBasicMaterial({ map: tex, transparent: true, depthWrite: false })
  }
  return aoMat
}

/** The AO skirt: one quad over the whole floor, dark where it meets the walls. */
function buildFloorAo(room: RoomDef, ox: number, oz: number): THREE.Mesh {
  const m = new THREE.Mesh(
    planeGeo(room.width + WALL_GAP * 2, room.depth + WALL_GAP * 2),
    wallAoMaterial(),
  )
  m.position.set(ox + room.width / 2, 0.012, oz + room.depth / 2)
  m.renderOrder = -2
  m.name = 'floorAo'
  return m
}

/**
 * Door posts, built PER SIDE and added to that side's wall group.
 *
 * They used to live outside the wall groups so a doorway stayed visible at every
 * rotation. That was the wrong trade: at the default rotation the east and south
 * walls are cut away, so their two door arches were left standing in the middle
 * of an open floor with nothing attached — one of them straight through the cake
 * stand. It read as a placement bug, in three separate scenes.
 *
 * A frame now hides with its wall, exactly like a real cut-away dollhouse. The
 * way out stays findable because the doorway MAT is a real placeable sitting on
 * the floor (see `exitMats.ts`): it is flat, it is bright, it never gets culled,
 * and unlike an arch a kid can actually tap it.
 */
function buildDoorFrames(room: RoomDef, side: Side, ox: number, oz: number, frameMat: THREE.Material): THREE.Group {
  const g = group(`doorFrames${side}`)
  for (const d of room.doors) {
    if (d.side !== side) continue
    const horizontal = d.side === 'N' || d.side === 'S'
    const fixed =
      d.side === 'N' ? oz - WALL_T / 2 - WALL_GAP
      : d.side === 'S' ? oz + room.depth + WALL_T / 2 + WALL_GAP
      : d.side === 'W' ? ox - WALL_T / 2 - WALL_GAP
      : ox + room.width + WALL_T / 2 + WALL_GAP
    const c = d.at + 0.5
    /*
     * WHAT IS ON THE OTHER SIDE OF THE DOOR.
     *
     * A doorway used to be a hole, and behind the hole was the backdrop — so
     * every door read as "a dark rectangle" with nothing beyond it. A dim warm
     * panel set into the opening, a shade darker than the wall, turns the hole
     * into a passage that goes somewhere. It sits just BEHIND the wall line so
     * the frame still reads as proud of it, and it hides with its own wall.
     */
    const beyondOff = WALL_T * 0.62
    const beyond =
      horizontal ?
        boxOn(DOOR_W, DOOR_H, 0.06, {
          color: 'woodDark',
          x: ox + c,
          z: fixed + (d.side === 'N' ? -beyondOff : beyondOff),
        })
      : boxOn(0.06, DOOR_H, DOOR_W, {
          color: 'woodDark',
          x: fixed + (d.side === 'W' ? -beyondOff : beyondOff),
          z: oz + c,
        })
    beyond.material = beyondMaterial()
    beyond.castShadow = false
    beyond.receiveShadow = false
    g.add(beyond)

    for (const s of [-1, 1]) {
      const off = c + (s * (DOOR_W + 0.22)) / 2
      const post =
        horizontal ?
          boxOn(0.22, DOOR_H + 0.14, WALL_T * 1.7, { color: 'woodDark', x: ox + off, z: fixed })
        : boxOn(WALL_T * 1.7, DOOR_H + 0.14, 0.22, { color: 'woodDark', x: fixed, z: oz + off })
      post.material = frameMat
      post.castShadow = false
      g.add(post)
    }
    // A slim header between the posts, so the pair reads as one opening rather
    // than two random sticks.
    const head =
      horizontal ?
        boxOn(DOOR_W + 0.44, 0.2, WALL_T * 1.7, { color: 'woodDark', x: ox + c, y: DOOR_H + 0.14, z: fixed })
      : boxOn(WALL_T * 1.7, 0.2, DOOR_W + 0.44, { color: 'woodDark', x: fixed, y: DOOR_H + 0.14, z: oz + c })
    head.material = frameMat
    head.castShadow = false
    g.add(head)
  }
  return g
}

let beyondMat: THREE.Material | null = null
/** The dim warm nothing seen through a doorway. Unlit — it must never sparkle. */
function beyondMaterial(): THREE.Material {
  if (beyondMat === null) {
    beyondMat = new THREE.MeshBasicMaterial({ color: mix('woodDark', 'ink', 0.45) })
  }
  return beyondMat
}

/**
 * The threshold a kid steps over, laid flat across the wall line.
 *
 * Lives in the shell ROOT, not in a wall group, so it survives the cut-away: at
 * two rotations out of four the doorway's wall is hidden, and without this the
 * only thing left saying "the door is here" is the mat. Together they read as a
 * door: sill in the floor, mat in front of it.
 *
 * ROUND-3: IT IS PAINTED IN THE DOOR-FRAME COLOUR, NOT THE SKIRTING COLOUR.
 *
 * It used to take the same `trimMat` as the floor surround it is set into, so it
 * was invisible — which is why the two mats standing against cut-away walls had
 * nothing at all to belong to and read as "scattered at room edges, not at
 * either doorway". A sill has to be a different tone from the board it is let
 * into, or it is not a sill. Two little jamb blocks bracket it, 12cm proud:
 * enough to say "gap in a wall", far too low to read as a door leaf standing in
 * mid-floor (which is what the round-2 arches were killed for).
 */
function buildThresholds(room: RoomDef, ox: number, oz: number, mat: THREE.Material): THREE.Group {
  const g = group('thresholds')
  const span = WALL_T + WALL_GAP * 2 + 0.16
  for (const d of room.doors) {
    const horizontal = d.side === 'N' || d.side === 'S'
    const fixed =
      d.side === 'N' ? oz - WALL_T / 2 - WALL_GAP
      : d.side === 'S' ? oz + room.depth + WALL_T / 2 + WALL_GAP
      : d.side === 'W' ? ox - WALL_T / 2 - WALL_GAP
      : ox + room.width + WALL_T / 2 + WALL_GAP
    const c = d.at + 0.5
    const board =
      horizontal ?
        boxOn(DOOR_W + 0.44, 0.06, span, { color: 'woodDark', x: ox + c, y: -0.01, z: fixed })
      : boxOn(span, 0.06, DOOR_W + 0.44, { color: 'woodDark', x: fixed, y: -0.01, z: oz + c })
    board.material = mat
    board.castShadow = false
    g.add(board)
    // The jambs: where the wall would have stopped, at ankle height.
    for (const s of [-1, 1]) {
      const off = c + (s * (DOOR_W + 0.3)) / 2
      const jamb =
        horizontal ?
          boxOn(0.16, 0.12, span, { color: 'woodDark', x: ox + off, y: 0.0, z: fixed })
        : boxOn(span, 0.12, 0.16, { color: 'woodDark', x: fixed, y: 0.0, z: oz + off })
      jamb.material = mat
      jamb.castShadow = false
      g.add(jamb)
    }
  }
  return g
}

/** The four corner posts. See the POST comment at the top of the file. */
function buildCornerPosts(room: RoomDef, ox: number, oz: number, mat: THREE.Material): Record<string, THREE.Object3D> {
  const line = WALL_T / 2 + WALL_GAP
  const xs = { W: ox - line, E: ox + room.width + line }
  const zs = { N: oz - line, S: oz + room.depth + line }
  const out: Record<string, THREE.Object3D> = {}
  for (const [ns, z] of Object.entries(zs)) {
    for (const [ew, x] of Object.entries(xs)) {
      const g = group(`post${ns}${ew}`)
      const shaft = boxOn(POST, POST_H, POST, { color: 'woodDark', x, z })
      shaft.material = mat
      shaft.castShadow = false
      /*
       * THE CAP IS THE CORNICE TURNING THE CORNER, NOT A HAT ON TOP OF IT.
       *
       * ROUND-3 FIX. It used to be 0.45 across, 0.1 tall and seated at POST_H so
       * its top finished flush with the wall top at 2.2 — while the cornice band
       * running into it is WALL_T + 0.05 = 0.27 across and finishes 0.03 lower.
       * Wider AND taller than the moulding it meets is a step you can see from
       * across the room: "the kitchen wall caps do not miter at the corner —
       * there is a visible step/notch where the two wall tops meet". The cap now
       * takes the cornice's own height, y and depth, so the three pieces are one
       * continuous run around the corner. Change `band(...)` at the foot of
       * `buildWall` and this has to change with it.
       */
      const capSize = POST + 0.06
      const cap = boxOn(capSize, 0.11, capSize, { color: 'woodDark', x, y: WALL_H - 0.14, z })
      cap.material = mat
      cap.castShadow = false
      // Cap only, no foot. A foot at the bottom of the post is buried in the
      // plinth lip at every camera rotation — four invisible draw calls per room
      // on a machine that has to run this for a three-year-old.
      g.add(shaft, cap)
      out[`${ns}${ew}`] = g
    }
  }
  return out
}

/**
 * THE DOORWAY MATS USED TO BE DRAWN HERE and are not any more. They are real
 * placeables now (`exitMat` in `src/content/furniture.ts`), for two reasons: a shell mesh cannot be
 * tapped (nothing in `statics` carries an instance id), so the only way out of
 * a bedroom was to walk onto the right floor tile and hope; and a flat mat is
 * the one exit cue that survives the wall cut-away without leaving an arch
 * standing in mid-floor.
 */

function buildGridHint(room: RoomDef, ox: number, oz: number): THREE.LineSegments {
  const pts: number[] = []
  for (let i = 0; i <= room.width; i++) {
    pts.push(ox + i, 0.02, oz, ox + i, 0.02, oz + room.depth)
  }
  for (let i = 0; i <= room.depth; i++) {
    pts.push(ox, 0.02, oz + i, ox + room.width, 0.02, oz + i)
  }
  const geo = new THREE.BufferGeometry()
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3))
  const ls = new THREE.LineSegments(geo, gridHintMaterial())
  ls.name = 'gridHint'
  return ls
}

export interface ShellHandle {
  readonly root: THREE.Group
  readonly walls: Record<Side, THREE.Object3D>
  /** Keyed 'NW' | 'NE' | 'SW' | 'SE'. Hidden only when BOTH walls are hidden. */
  readonly posts: Record<string, THREE.Object3D>
}

const shells = new Map<string, ShellHandle>()

/**
 * (Re)build a room's shell into its `statics` layer. Called on registration and
 * again whenever the kid repaints — cheap enough to just throw away and redo.
 */
export function buildShell(node: SceneNode, room: RoomDef, floorIdx: number, wallIdx: number): ShellHandle {
  const old = shells.get(room.id)
  if (old !== undefined) {
    node.statics.remove(old.root)
    shells.delete(room.id)
  }

  const g = node.grid
  const ox = g.originX
  const oz = g.originZ
  const fStyle = floorStyle(floorIdx)
  const wStyle = wallStyle(wallIdx)

  const root = group('roomShell')

  // -- plinth: the room is an OBJECT sitting in the world, not a cut-out ---
  const plinthW = room.width + (PLINTH + WALL_T + WALL_GAP) * 2
  const plinthD = room.depth + (PLINTH + WALL_T + WALL_GAP) * 2
  const plinth = boxOn(plinthW, 0.34, plinthD, {
    // Warm wood, NOT the sand of the rug it now stands on: the block under the
    // room has to separate from the ground, or the dollhouse reads as a decal.
    color: mix('woodLight', 'woodDark', 0.3),
    x: ox + room.width / 2,
    // Top lands 0.02 BELOW the floor plane. Flush would z-fight and the floor
    // texture would flicker away in bands — it did, once.
    y: -0.36,
    z: oz + room.depth / 2,
    name: 'plinth',
  })
  plinth.castShadow = false
  root.add(plinth)
  // A lighter lip around the top of the plinth catches the sun and gives the
  // base a readable edge instead of a flat brown skirt.
  const lip = boxOn(plinthW + 0.1, 0.07, plinthD + 0.1, {
    // Toned back toward the wood. At `sunWarm` 0.4 the tray around the room was
    // the brightest thing in the frame, and the room is supposed to be.
    color: mix('woodLight', 'sunWarm', 0.18),
    x: ox + room.width / 2,
    // Finishes 5mm PROUD of the plinth top, not flush with it. Flush is two
    // coplanar horizontal faces at y = -0.02 fighting for the same depth, and
    // the flicker that produces is the "thin white z-fighting line" the critic
    // caught along the far wall — same bug, different edge of the same room.
    y: -0.085,
    z: oz + room.depth / 2,
    name: 'plinthLip',
  })
  lip.castShadow = false
  root.add(lip)

  // -- floor -------------------------------------------------------------
  const trim = mix(wStyle.accent, 'woodDark', 0.42)
  const trimMat = toon(trim)
  /*
   * THE FLOOR IS EXACTLY THE ROOM, AND ONE PATTERN CELL IS ONE GRID TILE.
   *
   * It used to be `width + 2 * WALL_GAP` across with an INTEGER repeat count, so
   * a nine-tile room got two repeats of a four-cell pattern — eight and a bit
   * cells stretched over nine and a third units. Nothing lined up with anything:
   * the grid was clipped mid-tile at every edge and the grout ran on under the
   * wall base. Sizing the plane to the room and the repeat to `width / 4` makes
   * one pattern cell exactly one tile, and since rooms are a whole number of
   * tiles the edge always lands on a joint.
   *
   * The 16cm clearance ring the walls stand in is now a separate board in the
   * trim colour, which is what it should have been all along: a floor meets a
   * wall at a surround, not by dribbling half a tile underneath it.
   */
  const surround = boxOn(
    room.width + (WALL_GAP + WALL_T) * 2,
    0.08,
    room.depth + (WALL_GAP + WALL_T) * 2,
    {
      color: trim,
      x: ox + room.width / 2,
      // Top lands 1cm BELOW the floor plane. Flush at y=0 is a horizontal face
      // coplanar with the floor over the room's whole area, and the z-fight that
      // produced split the floor diagonally into two different materials — half
      // wood, half surround. Nothing in this file may finish exactly at y = 0.
      y: -0.09,
      z: oz + room.depth / 2,
      name: 'floorSurround',
    },
  )
  surround.material = trimMat
  surround.castShadow = false
  root.add(surround)

  const floorMat = surfaceMaterial(fStyle, surfaceRepeat(room.width), surfaceRepeat(room.depth))
  const floor = new THREE.Mesh(planeGeo(room.width, room.depth), floorMat)
  floor.position.set(ox + room.width / 2, 0, oz + room.depth / 2)
  floor.receiveShadow = true
  floor.name = 'roomFloor'
  root.add(floor)
  root.add(buildFloorAo(room, ox, oz))
  root.add(buildGridHint(room, ox, oz))

  // -- walls, with their door frames INSIDE them so both cut away together -
  const frameMat = toon(mix(wStyle.accent, 'woodDark', 0.62))
  // The sills take the DOOR-FRAME colour, not the skirting colour they used to
  // share with the surround they are let into. See `buildThresholds`.
  root.add(buildThresholds(room, ox, oz, frameMat))
  const walls: Record<Side, THREE.Object3D> = {
    N: group('sideN'),
    E: group('sideE'),
    S: group('sideS'),
    W: group('sideW'),
  }
  for (const s of ['N', 'E', 'S', 'W'] as const) {
    const side = walls[s] as THREE.Group
    side.add(buildWall(s, room, ox, oz, wallIdx, trim))
    side.add(buildDoorFrames(room, s, ox, oz, frameMat))
    noCast(side)
    root.add(side)
  }

  const posts = buildCornerPosts(room, ox, oz, frameMat)
  for (const p of Object.values(posts)) root.add(p)

  node.statics.add(root)
  const handle: ShellHandle = { root, walls, posts }
  shells.set(room.id, handle)
  return handle
}

/**
 * Which two walls the cut-away removes at this camera rotation.
 *
 * Falls back to rot 0 rather than returning undefined. Both callers run inside a
 * per-frame render system, another crew owns the camera, and a render system
 * that throws stops the engine tick for a kid who is in the middle of playing —
 * an unexpected rotation must cost a slightly wrong wall for one frame, never
 * the game. The DEV warning is so nobody has to guess where it came from.
 */
export function hiddenSides(rot: Rot): readonly Side[] {
  const sides = HIDDEN_BY_ROT[rot]
  if (sides !== undefined) return sides
  if (import.meta.env.DEV && !warnedRot.has(rot)) {
    warnedRot.add(rot)
    console.warn(`[interior] camera rotation ${String(rot)} is not a Rot — cut-away fell back to 0`)
  }
  return HIDDEN_BY_ROT[0]
}
const warnedRot = new Set<unknown>()

/** Apply the cut-away for the current camera rotation. Cheap, call per frame. */
export function applyCutaway(roomId: string, rot: Rot): void {
  const h = shells.get(roomId)
  if (h === undefined) return
  const hidden = hiddenSides(rot)
  for (const s of ['N', 'E', 'S', 'W'] as const) {
    h.walls[s].visible = !hidden.includes(s)
  }
  /*
   * ============ A POST ONLY EXISTS WHERE TWO WALLS MEET ============
   *
   * VR1: *"Both rooms terminate their right-hand wall in a raw brown cross-section
   * face with nothing capping it."* Photographed at (1620,560) in the kitchen and
   * (1720,600) in the zoo — and it is this line that put it there.
   *
   * A post used to stand while EITHER of its walls was up. Of the four corners,
   * ONE has both walls (the far corner), one has neither (the near corner, hidden),
   * and the other TWO have exactly one each — so every room grew two posts standing
   * in open air at the end of a wall run with nothing attached to them. That is
   * precisely the round-2 "orphan grey stub at each wall end" defect, re-created by
   * the fix for it: the reasoning ("it survives because it is a post") holds for a
   * post at a corner, and a corner needs two walls.
   *
   * A post now needs BOTH of its walls. What is left at the end of a cut run is the
   * run's own end face, in the room's own wallpaper, with the baseboard capping it
   * below and the cornice capping it above (both already run the full length to the
   * corner line — see `buildWall`). That is what a cut-away dollhouse looks like;
   * a tan column with no wall on it is what an unfinished one looks like.
   */
  for (const [key, obj] of Object.entries(h.posts)) {
    const ns = key[0] as Side
    const ew = key[1] as Side
    obj.visible = !hidden.includes(ns) && !hidden.includes(ew)
  }
}

export function shellOf(roomId: string): ShellHandle | undefined {
  return shells.get(roomId)
}
